'use client';

/* ============================================================================
 * Reveal — scroll-triggered entrance primitive
 * ============================================================================
 * Linear-style restrained motion: 12px lift + opacity fade, ~700ms easing
 * with `cubic-bezier(0.16, 1, 0.3, 1)` (the "spec" curve). Triggers once when
 * the element enters the viewport with a 15% margin so motion doesn't fire
 * before the user can actually see it.
 *
 * Usage:
 *   <Reveal>...</Reveal>                       // single element
 *   <Reveal delay={0.08}>...</Reveal>          // delay in seconds
 *   <Reveal as="h2" lift={20}>...</Reveal>     // override element / distance
 *   <Reveal eager>...</Reveal>                 // above-the-fold (LCP-safe)
 *
 *   <RevealStagger gap={0.06}>                 // children stagger
 *     <li>...</li><li>...</li><li>...</li>
 *   </RevealStagger>
 *
 * Crawler-safe SSR (the reason this file looks the way it does)
 * ------------------------------------------------------------
 * Earlier versions shipped each wrapper with `opacity:0` in the server HTML
 * and faded it in once `useInView` fired. That looked great for human
 * readers but invisible-content was a disaster for headless crawlers
 * (Googlebot, Screaming Frog, Sitebulb, Ahrefs Site Audit, Bingbot): they
 * execute JS at a fixed viewport size *without scrolling*, so anything
 * below the initial fold stayed at `opacity:0` forever in their snapshot.
 * Google can interpret that as user-cloaked content and either skip
 * indexing or down-weight the page. Our on-page SEO audit caught the site
 * shipping ~90 invisible elements per landing page.
 *
 * The fix below threads the needle:
 *
 *   1. SSR / pre-hydration: render the element with NO inline opacity or
 *      transform. Every crawler sees the content as fully present and
 *      visible, identical to what a scrolled human user sees.
 *   2. Client mount (`useLayoutEffect`, runs synchronously before paint):
 *      measure the element. If it's already in the viewport, mark it
 *      visible and we're done — no hide-then-reveal flicker for the hero.
 *      If it's below the fold, set inline `opacity:0; transform` (the user
 *      can't see it anyway because it's off-screen) and arm an
 *      IntersectionObserver.
 *   3. When the observer fires, transition to the visible state. The CSS
 *      `transition` property is added in the same paint as the property
 *      change, so the browser animates `opacity 0 → 1` and the lift back
 *      to zero — the same restrained "fade up" the brand always had.
 *
 * Result: crawlers see all content from the SSR HTML; humans still get the
 * scroll-triggered fade-up animation when they scroll into a section.
 *
 * Reduced-motion users get a synchronous, motion-free render (no transform,
 * no opacity transition) regardless of viewport position.
 * ========================================================================= */

import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
const DURATION_MS = 700;
const VIEWPORT_MARGIN = '0px 0px -15% 0px';

/* useLayoutEffect is the right tool for "measure the DOM before the browser
 * paints", but it logs a hydration warning during SSR. Swap to useEffect on
 * the server so the SSR pass stays quiet; the client picks the layout one. */
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/* Phases of a single Reveal:
 *   'ssr'    – pre-hydration / SSR.  Render with no inline animation styles.
 *   'hidden' – client decided the element is below the fold.  Snap to
 *              opacity:0 + lift transform; the user can't see it anyway.
 *   'shown'  – element is in (or has scrolled into) view.  Apply opacity:1
 *              transform:none with the CSS transition so the change animates. */
const PHASE_SSR = 'ssr';
const PHASE_HIDDEN = 'hidden';
const PHASE_SHOWN = 'shown';

function isElementInViewport(el) {
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  // Match the IntersectionObserver `-15%` bottom margin so the
  // synchronous and async checks agree on what "in view" means.
  const fold = vh - vh * 0.15;
  return rect.top < fold && rect.bottom > 0;
}

function buildStyle({phase, lift, duration, delay, baseStyle}) {
  if (phase === PHASE_SSR) return baseStyle;
  if (phase === PHASE_HIDDEN) {
    return {
      ...baseStyle,
      opacity: 0,
      transform: `translateY(${lift}px)`,
      // Intentionally no transition: snap to hidden, don't animate out.
      willChange: 'opacity, transform',
    };
  }
  // shown
  return {
    ...baseStyle,
    opacity: 1,
    transform: 'translateY(0)',
    transition: `opacity ${duration}ms ${EASE} ${delay}ms, transform ${duration}ms ${EASE} ${delay}ms`,
  };
}

export function Reveal({
  children,
  as: Tag = 'div',
  delay = 0,
  lift = 12,
  duration = DURATION_MS / 1000,
  className,
  style,
  eager = false,
  ...rest
}) {
  const ref = useRef(null);
  const [phase, setPhase] = useState(PHASE_SSR);
  const durationMs = Math.round(duration * 1000);
  const delayMs = Math.round(delay * 1000);

  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setPhase(PHASE_SHOWN);
      return undefined;
    }

    /* eager mode: the element is at the top of the page (hero / above the
     * fold).  Hide it for one frame, then animate.  This preserves the
     * subtle slide-up entrance without ever affecting LCP — the SSR HTML
     * still ships the content visible, so crawlers and the LCP heuristic
     * don't mind the brief on-load animation. */
    if (eager) {
      setPhase(PHASE_HIDDEN);
      const raf = requestAnimationFrame(() => {
        // Second RAF guarantees the 'hidden' style was committed and the
        // browser has a "before" value to transition from.
        requestAnimationFrame(() => setPhase(PHASE_SHOWN));
      });
      return () => cancelAnimationFrame(raf);
    }

    if (isElementInViewport(el)) {
      // Already in view — leave it visible (no animation, no hide flicker).
      setPhase(PHASE_SHOWN);
      return undefined;
    }

    setPhase(PHASE_HIDDEN);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setPhase(PHASE_SHOWN);
            observer.disconnect();
            return;
          }
        }
      },
      {rootMargin: VIEWPORT_MARGIN}
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [eager]);

  return (
    <Tag
      ref={ref}
      className={className}
      style={buildStyle({
        phase,
        lift,
        duration: durationMs,
        delay: delayMs,
        baseStyle: style,
      })}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* RevealStagger — fades in immediate children one-by-one with a `gap` (sec).
 *
 * Two modes:
 *   1. `mode="wrap"` (default) — each child is wrapped in <Reveal>. Simple
 *      but introduces an extra <div> per child, which is fine inside flex
 *      and grid containers but invalid for `<ul><li>` chains.
 *   2. `mode="clone"` — clones each child and animates it directly. No
 *      extra DOM wrapper, so safe for `<ul>` → `<li>` and `<dl>` → `<div>`
 *      patterns. The child must be a host element (string tag), not a
 *      component.
 *
 * Per-child computed delay keeps the consumer markup unchanged. */
export function RevealStagger({
  children,
  as: Tag = 'div',
  mode = 'wrap',
  gap = 0.06,
  initialDelay = 0,
  lift = 12,
  duration = DURATION_MS / 1000,
  className,
  style,
  eager = false,
  ...rest
}) {
  const items = React.Children.toArray(children);
  return (
    <Tag className={className} style={style} {...rest}>
      {items.map((child, i) => {
        const delay = initialDelay + i * gap;
        if (mode === 'clone') {
          return (
            <RevealClone
              key={child.key ?? i}
              delay={delay}
              lift={lift}
              duration={duration}
              eager={eager}
            >
              {child}
            </RevealClone>
          );
        }
        return (
          <Reveal
            key={child.key ?? i}
            delay={delay}
            lift={lift}
            duration={duration}
            eager={eager}
          >
            {child}
          </Reveal>
        );
      })}
    </Tag>
  );
}

/* RevealClone — turns a single host-element child into a revealed element
 * without adding an extra DOM wrapper. Used by RevealStagger mode="clone".
 * Same SSR-safe state machine as <Reveal>. */
function RevealClone({children, delay, lift, duration, eager = false}) {
  const ref = useRef(null);
  const [phase, setPhase] = useState(PHASE_SSR);
  const durationMs = Math.round(duration * 1000);
  const delayMs = Math.round(delay * 1000);

  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setPhase(PHASE_SHOWN);
      return undefined;
    }

    if (eager) {
      setPhase(PHASE_HIDDEN);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setPhase(PHASE_SHOWN));
      });
      return () => cancelAnimationFrame(raf);
    }

    if (isElementInViewport(el)) {
      setPhase(PHASE_SHOWN);
      return undefined;
    }

    setPhase(PHASE_HIDDEN);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setPhase(PHASE_SHOWN);
            observer.disconnect();
            return;
          }
        }
      },
      {rootMargin: VIEWPORT_MARGIN}
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [eager]);

  if (!React.isValidElement(children) || typeof children.type !== 'string') {
    // Fallback: not a host element, can't clone with a ref. Wrap.
    return (
      <Reveal delay={delay} lift={lift} duration={duration} eager={eager}>
        {children}
      </Reveal>
    );
  }

  const childStyle = children.props.style || {};
  return React.cloneElement(children, {
    ref,
    style: buildStyle({
      phase,
      lift,
      duration: durationMs,
      delay: delayMs,
      baseStyle: childStyle,
    }),
  });
}

/* RevealLine — animates a horizontal hairline drawing from left to right.
 *
 * Used by the section anchors to give every "01 / 12 ─── HERO" gutter a
 * subtle entrance flourish without animating the typography itself. Also
 * crawler-safe: SSR ships the line at full scale, the client clips it
 * post-hydration only when below the fold, then animates it in. */
export function RevealLine({className, duration = 0.9, delay = 0, eager = false}) {
  const ref = useRef(null);
  const [phase, setPhase] = useState(PHASE_SSR);
  const durationMs = Math.round(duration * 1000);
  const delayMs = Math.round(delay * 1000);

  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setPhase(PHASE_SHOWN);
      return undefined;
    }

    if (eager) {
      setPhase(PHASE_HIDDEN);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setPhase(PHASE_SHOWN));
      });
      return () => cancelAnimationFrame(raf);
    }

    if (isElementInViewport(el)) {
      setPhase(PHASE_SHOWN);
      return undefined;
    }

    setPhase(PHASE_HIDDEN);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setPhase(PHASE_SHOWN);
            observer.disconnect();
            return;
          }
        }
      },
      {rootMargin: '0px 0px -10% 0px'}
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [eager]);

  let lineStyle;
  if (phase === PHASE_SSR) {
    lineStyle = {transformOrigin: 'left center'};
  } else if (phase === PHASE_HIDDEN) {
    lineStyle = {
      transformOrigin: 'left center',
      transform: 'scaleX(0)',
      willChange: 'transform',
    };
  } else {
    lineStyle = {
      transformOrigin: 'left center',
      transform: 'scaleX(1)',
      transition: `transform ${durationMs}ms ${EASE} ${delayMs}ms`,
    };
  }

  return <span ref={ref} className={className} style={lineStyle} />;
}
